import { useEffect, useRef, useState } from 'react'

const SESSION_KEY = 'gritvue-objection-shown'
const TIME_ON_PAGE_MS = 45_000
const SCROLL_THRESHOLD = 0.35
/** Coming back to pricing or specs this many times without choosing also counts as hesitation. */
const REVISITS_NEEDED = 2

/**
 * Opens the objection question for visitors who are reading but not buying:
 * about 45 seconds on the page AND either a meaningful scroll or repeated
 * returns to pricing / specs, with no purchase funnel started. Never while
 * another modal is open, and never more than once per session.
 */
export function useObjectionTrigger() {
  const [open, setOpen] = useState(false)
  const [timeReady, setTimeReady] = useState(false)
  const [scrollReady, setScrollReady] = useState(false)
  const [revisits, setRevisits] = useState(0)
  const [hasIntent, setHasIntent] = useState(false)
  const [done, setDone] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.sessionStorage.getItem(SESSION_KEY) === '1'
  })
  const entered = useRef<Record<string, number>>({})

  useEffect(() => {
    const timer = window.setTimeout(() => setTimeReady(true), TIME_ON_PAGE_MS)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      if (window.scrollY / scrollable >= SCROLL_THRESHOLD) setScrollReady(true)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Count every fresh entry into pricing or specs; the second one is a revisit.
  useEffect(() => {
    const targets = ['offer', 'specs'].map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[]
    if (!targets.length) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const id = entry.target.id
          entered.current[id] = (entered.current[id] ?? 0) + 1
          if (entered.current[id] >= REVISITS_NEEDED) setRevisits((count) => count + 1)
        })
      },
      { threshold: 0.2 },
    )
    targets.forEach((target) => observer.observe(target))
    return () => observer.disconnect()
  }, [])

  // Any move toward buying disables the question for the rest of the session.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const el = event.target as HTMLElement | null
      if (!el) return
      if (el.closest('[data-buy-intent], [id^="pack-"][id$="-cta"]')) setHasIntent(true)
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  useEffect(() => {
    if (done || hasIntent || !timeReady) return
    if (!scrollReady && revisits === 0) return
    // Wait until no other dialog is open, then show once.
    const tryOpen = () => {
      if (document.querySelector('[aria-modal="true"]')) return false
      setOpen(true)
      setDone(true)
      window.sessionStorage.setItem(SESSION_KEY, '1')
      return true
    }
    if (tryOpen()) return
    const interval = window.setInterval(() => {
      if (tryOpen()) window.clearInterval(interval)
    }, 2000)
    return () => window.clearInterval(interval)
  }, [done, hasIntent, timeReady, scrollReady, revisits])

  // Reopened only on purpose, from "choose another" inside the recovery area.
  return { open, close: () => setOpen(false), reopen: () => setOpen(true) }
}
